/**
 * Role Preview Panel
 * Admin-only component to preview the app as different user roles
 * 
 * This is for demo purposes - it only changes UI display,
 * not actual permissions (which are enforced by Supabase RLS)
 */

import { useRolePreview, type PreviewRole } from '../lib/RolePreviewContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Eye, 
  EyeOff, 
  User, 
  UserCheck, 
  Shield, 
  Building2, 
  Package,
  AlertTriangle,
  Info
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const PREVIEW_ROLES: { 
  role: PreviewRole; 
  label: string; 
  description: string; 
  icon: typeof User;
  color: string;
}[] = [
  { 
    role: 'none', 
    label: 'My Real Role (Admin)', 
    description: 'View as yourself',
    icon: Shield,
    color: 'bg-purple-500',
  },
  { 
    role: 'guest', 
    label: 'Guest (Logged Out)', 
    description: 'Anonymous visitor - public pages only',
    icon: EyeOff,
    color: 'bg-gray-500',
  },
  { 
    role: 'patient', 
    label: 'Patient (Unverified)', 
    description: 'Logged in but no verification',
    icon: User,
    color: 'bg-blue-500',
  },
  { 
    role: 'patient_verified', 
    label: 'Patient (Verified)', 
    description: 'Completed clinic QR verification',
    icon: UserCheck,
    color: 'bg-green-500',
  },
  { 
    role: 'packet_verified', 
    label: 'Packet Verified', 
    description: 'Has at least one approved review',
    icon: Package,
    color: 'bg-amber-500',
  },
  { 
    role: 'clinic', 
    label: 'Clinic Staff', 
    description: 'Clinic dashboard access',
    icon: Building2,
    color: 'bg-teal-500',
  },
  { 
    role: 'producer', 
    label: 'Producer', 
    description: 'Product producer access',
    icon: Package,
    color: 'bg-indigo-500',
  },
];

export function RolePreviewPanel() {
  const { 
    previewRole, 
    isPreviewActive, 
    setPreviewRole, 
    clearPreview,
    canUseRolePreview 
  } = useRolePreview();

  if (!canUseRolePreview) {
    return null;
  }

  return (
    <Card className="border-2 border-dashed border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-950/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
              <Eye className="w-4 h-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Role Preview Mode</CardTitle>
              <CardDescription>Preview the app as different user roles</CardDescription>
            </div>
          </div>
          {isPreviewActive && (
            <Button variant="outline" size="sm" onClick={clearPreview}>
              <EyeOff className="w-4 h-4 mr-2" />
              Exit Preview
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
            <strong>For demos only.</strong> This changes what you see in the UI, 
            but your admin privileges remain active for all API operations.
          </AlertDescription>
        </Alert>

        {isPreviewActive && (
          <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Preview mode is active.</strong> You are viewing as: {' '}
              <Badge variant="secondary" className="ml-1">
                {PREVIEW_ROLES.find(r => r.role === previewRole)?.label}
              </Badge>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PREVIEW_ROLES.map(({ role, label, description, icon: Icon, color }) => {
            const isActive = previewRole === role;
            return (
              <Button
                key={role}
                variant={isActive ? "default" : "outline"}
                className={`h-auto py-3 px-4 justify-start gap-3 ${
                  isActive ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
                onClick={() => setPreviewRole(role)}
              >
                <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{label}</p>
                  <p className="text-xs text-muted-foreground truncate">{description}</p>
                </div>
                {isActive && (
                  <Badge variant="secondary" className="ml-auto flex-shrink-0">
                    Active
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

